import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  InjectOptions,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ModalComponent } from '@components/modal/modal.component';
import { NgSelectComponent, NgSelectConfig } from '@ng-select/ng-select';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Subscription } from 'rxjs';

const searchableSelectPlaceholder = $localize`Wybierz / wyszukaj...`;

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent implements OnChanges, AfterViewInit {
  @ContentChildren(TemplateRef) templates: QueryList<TemplateRef<HTMLElement>>;
  @Input() isMultiple? = false;
  @Input() isClearable = true;
  @Input() isSearchable = true;
  @Input() data: Record<string, unknown>[] | null;
  @Input() reactiveControl = new FormControl();
  @Input() inputId: string;
  @Input() displayProperty = ['nazwa', 'symbol'];
  @Input() valueProperty = 'id';
  @Input() placeholder?: string | null = searchableSelectPlaceholder;
  @Input() displayOnlyFirstProperty?: boolean = false;

  @Output() add = new EventEmitter<Record<string, unknown>>();
  @Output() remove = new EventEmitter<Record<string, unknown>>();
  @Output() valueChange = new EventEmitter<Record<string, unknown> | Record<string, unknown>[]>();

  @ViewChild('select') select: NgSelectComponent;

  protected readonly subscription = new Subscription();

  constructor(
    protected ngSelectConfig: NgSelectConfig,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly viewContainerRef: ViewContainerRef,
  ) {
    const checkParent = (el: HTMLElement): boolean => {
      if (el.className.includes('absolute') || el.className.includes('fixed') || el.className.includes('modal-body')) {
        return true;
      } else if (el.parentElement) {
        return checkParent(el.parentElement);
      }
    };
    const injectOptions: InjectOptions = { optional: true, skipSelf: true };
    const appModal = this.viewContainerRef.injector.get(ModalComponent, null, injectOptions);
    const ngxModal = this.viewContainerRef.injector.get(BsModalRef, null, injectOptions);
    const isInModal =
      appModal || (ngxModal && ngxModal.id) || checkParent(viewContainerRef.element.nativeElement as HTMLElement);
    this.ngSelectConfig.appendTo = isInModal ? 'body' : null;
    this.ngSelectConfig.clearSearchOnAdd = true;
    this.ngSelectConfig.clearAllText = $localize`Wyczyść`;
    this.ngSelectConfig.placeholder = null;
  }

  get multipleDisplayProperties(): boolean {
    return this.displayProperty.length > 1;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { placeholder, isSearchable } = changes;
    if (changes) {
      if (placeholder && typeof placeholder.currentValue === 'undefined') {
        this.placeholder = searchableSelectPlaceholder;
      }
      if (isSearchable && isSearchable.firstChange && isSearchable.currentValue === false && !placeholder) {
        this.placeholder = $localize`Wybierz...`;
      }
    }
  }

  ngAfterViewInit(): void {
    this.setInputPlaceholder();
  }

  onAdd(added?: Record<string, unknown>): void {
    this.add.emit(added);
    this.cdr.detectChanges();
  }

  onChange(change?: Record<string, unknown> | Record<string, unknown>[]): void {
    this.valueChange.emit(change);
    this.select.blur();
    this.cdr.detectChanges();
  }

  onRemove(removed?: Record<string, unknown>): void {
    this.remove.emit(removed);
    this.cdr.detectChanges();
  }

  getLabel(v1: string, v2: string): string | void {
    let label = '';
    if (v1) {
      label = v1;
    }
    if (v2) {
      label = label.concat(` (${v2})`);
    }
    return label;
  }

  isTooltipDisabled(el: HTMLElement) {
    const is = new BehaviorSubject(true);
    const parent = el?.parentElement;
    if (
      (parent?.className?.includes('ng-option') && parent?.className?.includes('ng-option-marked')) ||
      parent?.className?.includes('option-description')
    ) {
      if (el?.previousElementSibling?.tagName === 'INPUT') {
        is.next(el?.offsetWidth < parent?.clientWidth - 24);
      }
      is.next(!(el?.offsetWidth < el?.scrollWidth));
    } else if (parent?.className?.includes('ng-value')) {
      is.next(el?.offsetWidth < parent?.parentElement?.clientWidth - 48);
    }
    return is;
  }

  private setInputPlaceholder(): void {
    this.select.searchInput.nativeElement.placeholder = this.placeholder;
  }
}
